import { getToken } from '@/utils/auth';
import { BASE_URL } from '@/utils/constants';
import request from '@/utils/request';

const getLocalStorageLanguage = () => {
  return typeof window !== 'undefined'
    ? window.localStorage.getItem('language')
    : '';
};

const normalizeShortAiLanguage = (language) => {
  const localLanguage = getLocalStorageLanguage();
  if (!localLanguage) return 'zh';

  const normalizedLang = String(language || localLanguage).trim().toLowerCase();

  return /^en/.test(normalizedLang) ? 'en' : 'zh';
};

const getStreamingRequestLang = (lang) => {
  return normalizeShortAiLanguage(lang);
};

// 获取聊天记录
export const getChatHistory = (chatId) => {
  return request({
    url: `/api/standard/odata/UserCopilotChatHistories?$filter=user_copilot_chat_id eq '${chatId}'&$orderby=chat_time asc`,
    method: 'get'
  });
};

// 获取用户的会话列表 (支持分页)
export const getChatSessions = (userId, roleId, pageNum = 1, pageSize = 10) => {
  const skip = (pageNum - 1) * pageSize;
  let filter = `user_id eq '${userId}'`;
  if (roleId) {
    filter += ` and role_id eq '${roleId}'`;
  }
  return request({
    url: `/api/standard/odata/UserCopilotChats?$filter=${filter}&$orderby=chat_time desc&$skip=${skip}&$top=${pageSize}&$count=true`,
    method: 'get'
  });
};

// 保存会话Session
export const saveChatSession = (data) => {
  const payload = {
    session_id: data.session_id,
    user_id: data.user_id,
    role_id: data.role_id,
    chat_title: data.chat_title,
    chat_time: data.chat_time
  };
  return request({
    url: '/api/standard/odata/UserCopilotChats',
    method: 'post',
    data: payload
  });
};

// 删除会话Session
export const deleteChatSession = (id) => {
  return request({
    url: `/api/standard/odata/UserCopilotChats('${id}')`,
    method: 'delete'
  });
};

// 获取AI要点速览
export const getPhysicianAiSummary = (physicianId) => {
  return request({
    url: `/api/standard/odata/PhysicianAiSummaries?$filter=physician_id eq '${physicianId}'&$orderby=summary_time desc&$top=1`,
    method: 'get'
  });
};

// 保存AI要点速览
export const savePhysicianAiSummary = (data) => {
  return request({
    url: '/api/standard/odata/PhysicianAiSummaries',
    method: 'post',
    data: {
      physician_id: data.physician_id,
      summary: data.summary,
      summary_time: data.summary_time
    }
  });
};

// 获取拜访反馈总结
export const getPhysicianAiFeedbackSummary = (physicianId) => {
  return request({
    url: `/api/standard/odata/PhysicianAiFeedbackSummaries?$filter=physician_id eq '${physicianId}'&$orderby=summary_time desc&$top=1`,
    method: 'get'
  });
};

// 保存拜访反馈总结
export const savePhysicianAiFeedbackSummary = (data) => {
  return request({
    url: '/api/standard/odata/PhysicianAiFeedbackSummaries',
    method: 'post',
    data: {
      physician_id: data.physician_id,
      summary: data.summary,
      summary_time: data.summary_time
    }
  });
};

// 获取拜访策略
export const getPhysicianAiCallStrategy = (physicianId) => {
  return request({
    url: `/api/standard/odata/PhysicianAiCallStrategies?$filter=physician_id eq '${physicianId}'&$orderby=strategy_time desc&$top=1`,
    method: 'get'
  });
};

// 保存拜访策略
export const savePhysicianAiCallStrategy = (data) => {
  return request({
    url: '/api/standard/odata/PhysicianAiCallStrategies',
    method: 'post',
    data: {
      physician_id: data.physician_id,
      strategy: data.strategy,
      strategy_time: data.strategy_time
    }
  });
};

// 保存聊天记录
export const saveChatHistory = (data) => {
  const payload = {
    user_copilot_chat_id: data.user_copilot_chat_id,
    session_id: data.session_id,
    user_id: data.user_id,
    role_id: data.role_id,
    chat_role: data.chat_role,
    content: data.content,
    chat_time: data.chat_time
  };
  if (data.think_seconds !== undefined && data.think_seconds !== null) {
    payload.think_seconds = data.think_seconds;
  }
  if (data.total_seconds !== undefined && data.total_seconds !== null) {
    payload.total_seconds = data.total_seconds;
  }
  if (data.thoughts !== undefined && data.thoughts !== null) {
    payload.thoughts = data.thoughts;
  }
  return request({
    url: '/api/standard/odata/UserCopilotChatHistories',
    method: 'post',
    data: payload
  });
};

export const streamSuperRepCopilot = ({
  content,
  sessionId,
  agentName = 'SRCopilot',
  enableThinking = false,
  hasThoughts = false,
  onChunk,
  onComplete,
  onError
}) => {
  const payload = {
    content,
    session_id: sessionId || null,
    enable_thinking: Boolean(enableThinking),
    has_thoughts: Boolean(hasThoughts)
  };
  return streamBailianRequest(agentName || 'SRCopilot', payload, { onChunk, onComplete, onError });
};

const normalizeAiLanguage = (language) => {
  const localLanguage = getLocalStorageLanguage();
  if (!localLanguage) return 'zh-CN';

  if (!language) return '';
  if (/^en/i.test(language)) return 'en-US';
  if (/^zh/i.test(language)) return 'zh-CN';
  return language;
};

const shouldEmbedLanguageInContent = (agentName) => {
  return ['SRHCPSummary', 'SRHCPFeedback'].includes(agentName);
};

// 通用流式请求助手
const streamBailianRequest = async (agentName, payload, callbacks) => {
  const { onChunk, onComplete, onError } = callbacks;
  const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : `${BASE_URL}/`;
  const url = `${baseUrl}api/ai/Bailian/Agent/${agentName}`;
  const requestPayload = { ...payload };
  const normalizedLanguage = normalizeAiLanguage(requestPayload.language || requestPayload.locale);
  const embedLanguageInContent = shouldEmbedLanguageInContent(agentName) && typeof requestPayload.content === 'string';

  if (normalizedLanguage) {
    if (embedLanguageInContent) {
      const hasLanguageInContent = /(^|&)language=/.test(requestPayload.content);
      const contentLanguage = normalizeShortAiLanguage(requestPayload.language || requestPayload.locale);
      requestPayload.content = hasLanguageInContent
        ? requestPayload.content
        : `${requestPayload.content}&language=${encodeURIComponent(contentLanguage)}`;
    } else {
      requestPayload.language = normalizedLanguage;
    }
  }

  if (embedLanguageInContent) {
    delete requestPayload.language;
  }
  delete requestPayload.locale;

  try {
    const token = getToken()?.token;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        'Cache-Control': 'no-cache'
      },
      body: JSON.stringify({
        ...requestPayload,
        lang: getStreamingRequestLang(requestPayload && requestPayload.lang),
        incremental_output: true,
        sse: true
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let count = 0;
    while (count < Number.MAX_VALUE) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      if (onChunk) onChunk(chunk);

      count++;
    }

    if (onComplete) onComplete();
  } catch (error) {
    if (onError) onError(error);
  }
};

// 总结实时录入的拜访反馈 (Stream)
export const streamFeedbackSummary = ({ data, onChunk, onComplete, onError }) => {
  return streamBailianRequest('SRFeedbackSummary', data, { onChunk, onComplete, onError });
};

// SRHCPSummary (Stream)
export const streamHCPSummary = ({ data, onChunk, onComplete, onError }) => {
  return streamBailianRequest('SRHCPSummary', data, { onChunk, onComplete, onError });
};

// SRHCPFeedback (Stream)
export const streamHCPFeedback = ({ data, onChunk, onComplete, onError }) => {
  return streamBailianRequest('SRHCPFeedback', data, { onChunk, onComplete, onError });
};

// SRCallSummary (Stream)
export const streamCallSummary = ({ data, onChunk, onComplete, onError }) => {
  return streamBailianRequest('SRCallSummary', data, { onChunk, onComplete, onError });
};

// 获取下一步最佳行动建议 (Stream)
export const streamNextBestActionSuggestion = ({ data, onChunk, onComplete, onError }) => {
  return streamBailianRequest('SRNextBestActionSuggestion', data, { onChunk, onComplete, onError });
};

// 获取下一步最佳行动建议
export const getNextBestActionSuggestion = (data = {}) => {
  const payload = { ...data };

  return request({
    url: '/api/ai/Bailian/Agent/SRNextBestActionSuggestion',
    method: 'post',
    data: {
      content: payload.content || '',
      lang: getStreamingRequestLang(payload && payload.lang),
      incremental_output: false,
      sse: false
    }
  });
};

// 获取用户最新客户动态总结
export const getUserRecentHcpUpdateSummary = (userId, roleId) => {
  return request({
    url: `/api/standard/odata/UserRecentHcpUpdateSummaries?$filter=user_id eq '${userId}' and role_id eq '${roleId}'&$orderby=create_time desc&$top=1`,
    method: 'get'
  });
};

// 保存用户最新客户动态总结
export const saveUserRecentHcpUpdateSummary = (data) => {
  return request({
    url: '/api/standard/odata/UserRecentHcpUpdateSummaries',
    method: 'post',
    data: {
      user_id: data.user_id,
      role_id: data.role_id,
      summary: data.summary,
      create_time: data.create_time
    }
  });
};

// SRMyHCPUpdates (Stream)
export const streamMyHCPUpdates = ({ data, onChunk, onComplete, onError }) => {
  const baseContent = data && data.content ? String(data.content).trim() : '';
  const isEnglish = getStreamingRequestLang(data && data.lang) === 'en';
  const detailInstruction = (isEnglish
    ? [
      'Use the exact structure below. Only use facts returned by system tools or data sources; never invent customer names:',
      '【Recent Activity Summary】',
      '- Use at most 3 bullets to summarize the number of updated customers, main activity types, and priorities.',
      '【Recent Activity Details】',
      '- List every updated customer returned by the search. Even when there are more than 20, do not replace customer names with only a total count.',
      '- Use one bullet per customer in this format: Customer name | Institution (if available) | Activity type | Summary | Date or source (if available).',
      '- Merge multiple updates for the same customer. Use "N/A" for a missing field, but never omit the customer name.',
      '【Next Action Suggestions】',
      '- Name the relevant customer in every suggestion and tie the action to a specific update above.'
    ]
    : [
      '请严格按以下结构输出，并只使用系统工具或数据源中真实存在的信息，不得编造客户姓名：',
      '【近期动态汇总】',
      '- 最多3条，概括客户更新数量、主要动态类型和需要优先关注的事项。',
      '【近期动态详情】',
      '- 必须逐一列出本次检索到的全部更新客户；即使有20位以上，也不能只写“20多位客户”而省略姓名。',
      '- 每位客户单独一条，格式为：客户姓名｜机构（如有）｜动态类型｜动态摘要｜日期或来源（如有）。',
      '- 同一客户有多条动态时合并在该客户条目下；缺失字段写“暂无”，但客户姓名不可省略。',
      '【下一步行动建议】',
      '- 建议必须指明对应客户姓名，并与上面的具体动态关联。'
    ]).join('\n');
  const requestData = {
    ...(data || {}),
    content: [baseContent, detailInstruction].filter(Boolean).join('\n\n')
  };

  return streamBailianRequest('SRMyHCPUpdates', requestData, { onChunk, onComplete, onError });
};
